import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/lib/lang";
import { Eyebrow, MaskLines, Reveal } from "@/components/motion";
import { ZMark } from "@/components/ZMark";

export const About = () => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const rotate = useTransform(scrollYProgress, [0, 1], [-40, 60]);
  const y = useTransform(scrollYProgress, [0, 1], ["20%", "-20%"]);

  return (
    <section ref={ref} id="about" data-testid="about-section" className="relative overflow-hidden py-24 sm:py-40">
      <motion.div style={{ rotate, y }} className="pointer-events-none absolute -left-40 top-10 opacity-[0.07]">
        <ZMark id="about-z" className="h-[36rem] w-[36rem]" />
      </motion.div>
      <div className="container-x relative grid gap-16 lg:grid-cols-12">
        <div className="self-start lg:sticky lg:top-32 lg:col-span-6">
          <Eyebrow>{t.about.eyebrow}</Eyebrow>
          <h2 key={lang} data-testid="about-title" className="section-title mt-6 font-display font-bold">
            <MaskLines inView lines={t.about.titleLines} lineClassName={(i) => (i === 2 ? "text-z-gradient" : "")} />
          </h2>
        </div>
        <div className="space-y-10 lg:col-span-6 lg:pt-20">
          <Reveal as="p" className="text-xl font-light leading-relaxed text-white/85 sm:text-2xl">{t.about.p1}</Reveal>
          <Reveal as="p" delay={0.1} className="text-base leading-relaxed text-white/55 sm:text-lg">{t.about.p2}</Reveal>
          <ul className="border-t border-white/10">
            {t.about.principles.map(([k, v], i) => (
              <Reveal as="li" key={k} delay={i * 0.08} data-testid={`about-principle-${i}`} className="group grid grid-cols-12 gap-4 border-b border-white/10 py-7">
                <span className="col-span-2 pt-2 font-mono text-xs text-white/35">0{i + 1}</span>
                <span className="col-span-10 font-display text-2xl font-semibold tracking-tight transition-transform duration-500 ease-[cubic-bezier(.16,1,.3,1)] group-hover:translate-x-2 sm:col-span-4">{k}</span>
                <span className="col-span-12 text-sm leading-relaxed text-white/60 sm:col-span-6 sm:text-base">{v}</span>
              </Reveal>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};
